<template>
  <div
    :id="dom_id"
    class="GodMap"
    :class="{
      cn: $i18n.locale === `zh-CN`
    }"
  ></div>
</template>

<script setup>
import { renderAutoPause } from './utils/map-utils';
import { darkStartMap, lightStartMap, darkEndMap, lightEndMap } from './consts/images';
import { activityDetailStore } from '@client/store/activityDetailStore';
import { computed, nextTick, reactive, watch } from 'vue';
import { useMap } from './hooks/useMap';
import { useSessionStorage } from '@vueuse/core';
const sessionState = useSessionStorage('isWGS84', 0);
import { getGodvMapConstructor } from './utils/get-godv';
import { initStore } from '@client/store/initStore';
import { activeTab } from '../../hooks/useActivityTabs';
import { newListData } from '../../main/table/hooks/useLapData';

const activityDetailStoreRef = activityDetailStore();
const initStoreRef = initStore();
const THEME = initStoreRef.theme;
sessionState.value = 0;
const state = reactive({
  underLines: [], // 底线数组

  fullPoint: [], // 全段坐标点

  selectedPath: [], // 高亮圈路线数组

  circleArr: [], // 圈标记数组

  polyline: [], // 运动路径数组

  stopLine: [], // 暂停区间数组

  snowLine: [], // 滑雪缆车区间数组

  markerStart: null, // 起点坐标

  markerEnd: null, // 终点坐标

  roadPoint: null // 周柏豪蓝色点图表联动点,
});

const { dom_id, markLap, sportMap, stateMap, pakHo, snowStop, initMarkLap, bindZoomChange } = useMap({ setPakHoPoint });

const activityDetail = computed(() => activityDetailStoreRef.activityDetail);
watch(activityDetail, () => {
  nextTick(async () => {
    // console.log('activityDetail change');
    // 取消数据监听重新渲染地图
  });
});

watch(activeTab, () => {
  nextTick(() => {
    console.log('activeTab');
    clearMap();
    makeInit();
  });
});
watch(sessionState, async (val) => {
  if (val) {
    stateMap.value.setFeatures(['bg', 'road']);
  } else {
    stateMap.value.setFeatures(['bg', 'point', 'road']);
  }
  removeEvent();
  clearUnderLines();
  clearMap();
  redrawMap();
});
function removeEvent() {
  stateMap.value.clearEvents('zoomchange');
  stateMap.value.clearMap();
}
async function redrawMap() {
  await initMarkLap();
  await makeOriginLine();
  await makeInit();
  initLapMarkers();
  setPakHoPoint(pakHo.value, 'reinit');
  // 设置选中高亮线
  setSelectData(selectedData.value);
}
watch(sportMap, async () => {
  // console.log('sportMap change');
  //  init
  await initMarkLap();
  //  设置默认线
  makeOriginLine();
  //  初始化地图
  await makeInit();
  setPakHoPoint(pakHo.value);
});
function clearUnderLines() {
  stateMap.value.remove(state.underLines);
  state.underLines = [];
}
// 清空地图
function clearMap() {
  state.selectedPath && stateMap.value.remove(state.selectedPath);
  state.circleArr && stateMap.value.remove(state.circleArr);
  state.polyline && stateMap.value.remove(state.polyline);
  state.stopLine && stateMap.value.remove(state.stopLine);
  state.snowLine && stateMap.value.remove(state.snowLine);
  state.markerStart && stateMap.value.remove(state.markerStart);
  state.markerEnd && stateMap.value.remove(state.markerEnd);

  state.fullPoint = [];
  state.selectedPath = [];
  state.circleArr = [];
  state.polyline = [];
  state.stopLine = [];
  state.snowLine = [];

  state.markerStart = null;
  state.markerEnd = null;
}
// init地图
async function makeInit() {
  //  设置显示线
  await makeShowLine();

  //  起点坐标
  if (state.markerStart) {
    state.markerStart.setMap(null);
  }
  state.markerStart = await makeStartMarker(state.fullPoint[0]);

  //  终点坐标
  if (state.markerEnd) {
    state.markerEnd.setMap(null);
  }
  state.markerEnd = await makeEndMarker(state.fullPoint[state.fullPoint.length - 1]);

  //  设置有缆车情况下的缆车路线
  if (snowStop.value.length > 0) {
    state.snowLine = [];
    for (let i = 0; i < snowStop.value.length; i++) {
      const path = [];
      for (let j = 0; j < snowStop.value[i].length; j++) {
        path.push(sportMap.value.transformedGps[snowStop.value[i][j].timestamp]);
      }
      if (path.length) {
        const GmapClass = await getGodvMapConstructor();
        const carLine = new GmapClass.Polyline({
          path,
          isOutline: false,
          borderWeight: 3,
          strokeColor: THEME === 'theme-dark' ? `#5C9E3C` : `#96D407`,
          strokeOpacity: 1,
          strokeWeight: 6,
          strokeStyle: 'solid',
          strokeDasharray: [10, 5],
          lineJoin: 'round',
          lineCap: 'round',
          zIndex: 50
        });
        state.snowLine.push(carLine);
      }
    }
    //  设置地图渲染
    stateMap.value.add(state.snowLine);
  }

  const { frequencyList, pauseList } = activityDetailStoreRef.activityDetail;
  renderAutoPause({
    map: stateMap.value,
    points: JSON.parse(JSON.stringify(frequencyList)),
    pauses: JSON.parse(JSON.stringify(pauseList)),
    theme: THEME,
    transformedGps: sportMap.value.transformedGps,
    isGoogle: false
  });

  // TODO: todo_xjf
  // 这里不加 timeout 会导致生产的 Markers 不对
  // 原因是加载的时候 zoomLevel 跟旧版不一样了
  setTimeout(() => {
    initLapMarkers();
    sportMap.value.renderCheckPoints();
  }, 1000);
}

// 是否显示地图卡片
const mapCardVisible = computed(() => activityDetailStoreRef.mapCardVisible);
function initLapMarkers() {
  // 未显示地图
  if (!mapCardVisible.value) return;
  // 未完成初始化
  if (!markLap.value) return;

  const activityData = JSON.parse(JSON.stringify(activityDetailStoreRef.activityDetail));
  const originalLapDatas = activityData.lapList;
  const allActivties = activityData.frequencyList;
  markLap.value
    .setSummary(activityData.summary)
    .calcMaximum4markers(allActivties)
    .calcLaps2mark(originalLapDatas, allActivties)
    .renderMarker4laps();

  bindZoomChange();
}
// 生成底线
async function makeOriginLine() {
  const GmapClass = await getGodvMapConstructor();
  const transformedGps = sportMap.value.transformedGps;
  //  底线
  if (activityDetailStoreRef._detail?.handStop.length > 0) {
    const handStop = activityDetailStoreRef._detail?.handStop;
    for (let i = 0; i < handStop.length; i++) {
      const path = [];
      for (let j = 0; j < handStop[i].length; j++) {
        const item = handStop[i][j];
        if (item.gpsLon === 0 || item.gpsLat === 0 || !transformedGps[item.timestamp]) continue;

        path.push(transformedGps[item.timestamp]);
      }
      if (!path.length) continue;
      const line = new GmapClass.Polyline({
        path,
        isOutline: false,
        borderWeight: 3,
        strokeColor: THEME === 'theme-dark' ? `#A0B341` : `#E2EBB7`,
        strokeOpacity: 1,
        strokeWeight: 6,
        strokeStyle: 'solid',
        strokeDasharray: [10, 5],
        lineJoin: 'round',
        lineCap: 'round',
        zIndex: 49
      });
      state.underLines.push(line);
    }
  } else {
    const path = [];
    for (let i = 0; i < activityDetailStoreRef._detail?.frequencyList.length; i++) {
      const item = activityDetailStoreRef._detail?.frequencyList[i];
      if (transformedGps[item.timestamp] === null) continue;
      path.push(transformedGps[item.timestamp]);
    }
    if (!path.length) return;
    const line = new GmapClass.Polyline({
      path,
      isOutline: false,
      borderWeight: 3,
      strokeColor: THEME === 'theme-dark' ? `#A0B341` : `#E2EBB7`,
      strokeOpacity: 1,
      strokeWeight: 6,
      strokeStyle: 'solid',
      strokeDasharray: [10, 5],
      lineJoin: 'round',
      lineCap: 'round',
      zIndex: 49
    });
    state.underLines.push(line);
  }
  //  设置地图渲染
  // console.log(`xjf🔔`, JSON.parse(JSON.stringify(state.underLines)));
  stateMap.value.add(state.underLines);
}
// 生成运动路径
async function makeShowLine() {
  const GmapClass = await getGodvMapConstructor();
  if (activityDetailStoreRef.activityDetail?.handStop.length > 0) {
    for (let i = 0; i < activityDetailStoreRef.activityDetail.handStop.length; i++) {
      const path = [];
      for (let j = 0; j < activityDetailStoreRef.activityDetail.handStop[i].length; j++) {
        const item = activityDetailStoreRef.activityDetail.handStop[i][j];
        const { gpsLon, gpsLat, timestamp } = item;
        if (gpsLon === 0 || gpsLat === 0) continue;

        const position = sportMap.value.transformedGps[timestamp];
        path.push(position);
        state.fullPoint.push(position);
      }
      if (!path.length) continue;
      const line = new GmapClass.Polyline({
        path,
        isOutline: false,
        borderWeight: 3,
        strokeColor: THEME === 'theme-dark' ? `#DDFF00` : `#BDD707`,
        strokeOpacity: 1,
        strokeWeight: 6,
        strokeStyle: 'solid',
        strokeDasharray: [10, 5],
        lineJoin: 'round',
        lineCap: 'round',
        zIndex: 49
      });

      state.polyline.push(line);
    }
  } else {
    const path = [];
    for (let i = 0; i < activityDetailStoreRef.activityDetail.frequencyList.length; i++) {
      const point = sportMap.value.transformedGps[activityDetailStoreRef.activityDetail.frequencyList[i].timestamp];
      if (!point) continue;
      path.push(point);
      state.fullPoint.push(point);
    }
    if (!path.length) return;
    const line = new GmapClass.Polyline({
      path,
      isOutline: false,
      borderWeight: 3,
      strokeColor: THEME === 'theme-dark' ? `#DDFF00` : `#BDD707`,
      strokeOpacity: 1,
      strokeWeight: 6,
      strokeStyle: 'solid',
      strokeDasharray: [10, 5],
      lineJoin: 'round',
      lineCap: 'round',
      zIndex: 49
    });
    state.polyline.push(line);
  }

  //  设置地图渲染
  stateMap.value.add(state.polyline);

  //  设置合适的观看角度
  stateMap.value.setFitView(state.polyline);
}
// 生成起点标记
async function makeStartMarker(path) {
  const GmapClass = await getGodvMapConstructor();
  return new GmapClass.Marker({
    map: stateMap.value,
    position: path,
    anchor: 'bottom-center',
    icon: THEME === 'theme-dark' ? darkStartMap : lightStartMap
    // offset: new GmapClass.Pixel(4, 6),
    // zIndex: 100,
  });
}
// 生成终点标记
async function makeEndMarker(path) {
  const GmapClass = await getGodvMapConstructor();
  return new GmapClass.Marker({
    map: stateMap.value,
    position: path,
    anchor: 'bottom-center',
    icon: THEME === 'theme-dark' ? darkEndMap : lightEndMap
    // offset: new GmapClass.Pixel(-16, -32),
    // zIndex: 100
  });
}
// 生成高亮线
async function makeHeightLine(paths) {
  const GmapClass = await getGodvMapConstructor();
  return new GmapClass.Polyline({
    path: paths,
    isOutline: false,
    borderWeight: 3,
    strokeColor: THEME === 'theme-dark' ? `#DDFF00` : `#BDD707`,
    strokeOpacity: 1,
    strokeWeight: 6,
    strokeStyle: 'solid',
    strokeDasharray: [10, 5],
    lineJoin: 'round',
    lineCap: 'round',
    zIndex: 52
  });
}

const selectedData = computed(() => activityDetailStoreRef.selectedData);
watch(selectedData, () => {
  setTimeout(() => {
    setSelectData(selectedData.value);
  });
});

// 设置地图上的高亮线
async function setSelectData(val) {
  if (state.selectedPath.length > 0) {
    stateMap.value.remove(state.selectedPath);
    state.selectedPath = [];
  }
  if (val.length > 0) {
    let _selectedPath = state.selectedPath;
    const selectItems = val.map((listRowIndex) => {
      return newListData.value.find((i) => i.listRowIndex === listRowIndex);
    });

    const points = activityDetailStoreRef.activityDetail?.frequencyList;
    for (let i = 0; i < val.length; i++) {
      const selectItem = selectItems[i];

      const s = selectItem.startTimestamp;
      const e = selectItem.endTimestamp;
      const line = [];
      let sock = 0;
      for (let j = sock; j < points.length; j++) {
        const x = points[j].timestamp;
        if (s <= x && x <= e) {
          const aMapGps = sportMap.value.transformedGps[points[j].timestamp];
          line.push(aMapGps);
        }
        if (x > e) {
          sock = j - 1;
          break;
        }
      }
      const pl = await makeHeightLine(line);
      _selectedPath.push(pl);
    }
    state.polyline.forEach((obj) => {
      obj.setOptions({
        strokeColor: THEME === 'theme-dark' ? `#A0B341` : `#E2EBB7`
      });
    });
    state.selectedPath = _selectedPath;
    console.log(`🔔 _selectedPath`, _selectedPath);
    stateMap.value.add(state.selectedPath);
    stateMap.value.setFitView(state.selectedPath);
  } else {
    state.polyline.forEach((obj) => {
      obj.setOptions({
        strokeColor: THEME === 'theme-dark' ? `#DDFF00` : `#BDD707`
      });
    });
    stateMap.value.setFitView(state.polyline);
  }
}
// 设置鼠标当前在图表上对应的点
function setPakHoPoint(val, reinit) {
  if (!val || !val.data) {
    return;
  }
  sportMap.value.setGangedMarker(val.data, reinit);
}
</script>

<style lang="scss" scoped>
.GodMap {
  width: 100%;
  height: 100%;
  background: #fcf9f3 !important;

  &.cn {
    background: #020203 !important;
  }

  :deep(.amap-maptype-list) {
    display: none !important;
  }

  :deep(.amap-logo) {
    z-index: 0;
  }
  :deep(.amap-copyright) {
    opacity: 0;
  }

  :deep(.amap-maptypecontrol) {
    top: unset;
    bottom: 68px;
    right: 10px;
  }
}
</style>
